import { useState } from "react";
import { Link } from "react-router-dom";
import { FileSignature, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Empty, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function Detail({ id, onClose, onDone }) {
  const { data: tc } = useFetch(`/tele/provider/consults/${id}`);
  const decline = async () => {
    const text = window.prompt("Reason (sent to the client)?");
    if (!text) return;
    try { await api.post(`/tele/provider/consults/${id}/decline`, { text }); toast.success("Request declined · client emailed"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl" data-testid="tele-detail-dialog">
        <DialogHeader><DialogTitle>Remote consultation</DialogTitle></DialogHeader>
        {!tc ? <Spinner /> : (
          <div className="space-y-4 text-sm">
            <p><b>{tc.patient.name}</b> · {tc.patient.age} yrs · {tc.patient.address}<br /><span className="text-xs text-slate-500">{tc.package_name} · sent {fmtDateTime(tc.submitted_at)}</span></p>
            <div><p className="label">GIA summary</p><pre className="whitespace-pre-wrap rounded-xl bg-purple-50 p-3 font-sans text-xs" data-testid="tele-summary">{tc.summary || "Summary unavailable — see conversation."}</pre></div>
            <details><summary className="cursor-pointer text-xs font-semibold">Full conversation ({tc.messages.length})</summary>
              <div className="mt-2 space-y-1">{tc.messages.map((m, i) => <p key={i} className="text-xs"><b>{m.role === "user" ? "Patient" : "GIA"}:</b> {m.content}</p>)}</div></details>
            {tc.status === "SUBMITTED" ? (
              <div className="flex gap-2">
                <button className="btn-secondary flex-1" onClick={decline} data-testid="tele-decline-button"><X size={14} /> Decline</button>
                <Link to={`/provider/rx/new?tc=${tc.id}`} className="btn-primary flex-1" data-testid="tele-write-rx-button"><FileSignature size={14} /> Approve & write e-RX</Link>
              </div>
            ) : <StatusBadge status={tc.status} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Teleconsults() {
  const [status, setStatus] = useState("SUBMITTED");
  const { data, reload } = useFetch(`/tele/provider/queue?status=${status}`);
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-4">
      <PageHead eyebrow="Doctor Portal" title="Remote consultations" sub="Clients described their symptoms to GIA. Approving means issuing an e-RX; it's emailed to the client and uses one online consultation."
        actions={<select className="field w-40" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="tele-status-filter">{["SUBMITTED", "APPROVED", "DECLINED"].map((s) => <option key={s}>{s}</option>)}</select>} />
      {!data ? <Spinner /> : !data.length ? <Empty title="No requests" /> : (
        <div className="surface overflow-x-auto"><table className="data-table" data-testid="tele-queue-table">
          <thead><tr><th>Patient</th><th>Age</th><th>Package</th><th>Sent</th><th>Status</th></tr></thead>
          <tbody>{data.map((t) => (
            <tr key={t.id} className="cursor-pointer" onClick={() => setOpen(t.id)} data-testid={`tele-row-${t.id}`}>
              <td className="font-semibold">{t.patient.name}</td><td>{t.patient.age}</td><td className="text-xs">{t.package_name}</td><td className="text-xs">{fmtDateTime(t.submitted_at)}</td><td><StatusBadge status={t.status} /></td></tr>
          ))}</tbody>
        </table></div>
      )}
      {open && <Detail id={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); reload(); }} />}
    </div>
  );
}
